/**
 * useNotifications Hook
 *
 * Polls for specification notifications (drift alerts, approval changes,
 * scan completions, etc.) at a configurable interval.
 *
 * Part of Real-Time Drift Notifications — brand-agnostic platform feature.
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getSpecNotifications, type SpecNotification } from '../api/specifications';
import { isSuccessResponse } from '../api/apiClient';

export interface UseNotificationsReturn {
  notifications: SpecNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  refresh: () => void;
}

/**
 * Custom hook for polling spec notifications
 *
 * @param clientId - Client UUID to fetch notifications for
 * @param pollIntervalMs - Polling interval in ms (default 30000)
 */
export function useNotifications(
  clientId: string | null,
  pollIntervalMs = 30000
): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<SpecNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getSpecNotifications(clientId, { status: 'unread' });

      if (isSuccessResponse(response)) {
        const records = response.data?.Records ?? [];
        setNotifications(records);
      }
      // Non-success responses silently ignored (endpoint may not be deployed yet)
    } catch {
      // Silently swallow — notifications are non-critical and endpoint
      // may not be deployed yet (P3 stack). No console noise.
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Initial fetch + polling interval
  useEffect(() => {
    if (!clientId) return;

    // Fetch immediately
    fetchNotifications();

    // Set up polling
    intervalRef.current = setInterval(fetchNotifications, pollIntervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [clientId, pollIntervalMs, fetchNotifications]);

  /**
   * Optimistic mark-as-read for a single notification.
   * Updates local state immediately. A future PUT handler will persist this.
   */
  const markAsRead = useCallback((notificationId: number) => {
    setNotifications(prev =>
      prev.map(n =>
        n.Notification_ID === notificationId
          ? { ...n, Status: 'read' as const, Read_At: new Date().toISOString() }
          : n
      )
    );
  }, []);

  /**
   * Optimistic mark-all-as-read.
   * Updates local state immediately. A future PUT handler will persist this.
   */
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({
        ...n,
        Status: 'read' as const,
        Read_At: n.Read_At || new Date().toISOString()
      }))
    );
  }, []);

  const unreadCount = notifications.filter(n => n.Status === 'unread').length;

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refresh: fetchNotifications
  };
}
